import React, { useState, useEffect } from 'react';
import { chatbotApi } from '../services/api';
import { ChatbotFlow } from '../types';
import './ChatbotFlows.css';

const ChatbotFlows: React.FC = () => {
  const [flows, setFlows] = useState<ChatbotFlow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingFlow, setEditingFlow] = useState<ChatbotFlow | null>(null);
  const [showInstructions, setShowInstructions] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    trigger_keyword: '',
    flow_data: { steps: [] },
    is_active: true,
  });

  useEffect(() => {
    loadFlows();
  }, []);

  const loadFlows = async () => {
    try {
      setLoading(true);
      const data = await chatbotApi.getAll();
      setFlows(data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al cargar flujos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      if (editingFlow) {
        await chatbotApi.update(editingFlow.id!, formData);
      } else {
        await chatbotApi.create(formData);
      }
      setShowForm(false);
      setEditingFlow(null);
      setFormData({ name: '', description: '', trigger_keyword: '', flow_data: { steps: [] }, is_active: true });
      loadFlows();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al guardar flujo');
    }
  };

  const handleEdit = (flow: ChatbotFlow) => {
    setEditingFlow(flow);
    setFormData({
      name: flow.name,
      description: flow.description || '',
      trigger_keyword: flow.trigger_keyword || '',
      flow_data: flow.flow_data || { steps: [] },
      is_active: flow.is_active !== false,
    });
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('¿Estás seguro de eliminar este flujo?')) return;

    try {
      await chatbotApi.delete(id);
      loadFlows();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al eliminar flujo');
    }
  };

  return (
    <div className="chatbot-flows">
      <div className="section-header">
        <h2>Gestión de Flujos de Chatbot</h2>
        <div className="header-actions">
          <button onClick={() => setShowInstructions(!showInstructions)} className="btn-info">
            {showInstructions ? 'Ocultar' : 'Ver'} Instructivo
          </button>
          <button onClick={() => { setShowForm(true); setEditingFlow(null); setFormData({ name: '', description: '', trigger_keyword: '', flow_data: { steps: [] }, is_active: true }); }} className="btn-primary">
            Nuevo Flujo
          </button>
        </div>
      </div>

      {showInstructions && (
        <div className="instructions-panel">
          <h3>📖 Instructivo: Cómo Crear y Editar Flujos de Chatbot</h3>
          
          <div className="instruction-section">
            <h4>1. ¿Qué es un Flujo de Chatbot?</h4>
            <p>Un flujo es una secuencia automatizada de mensajes que se activa cuando un usuario envía una palabra clave específica. El chatbot responderá automáticamente siguiendo los pasos definidos en el flujo.</p>
          </div>

          <div className="instruction-section">
            <h4>2. Crear un Nuevo Flujo</h4>
            <ol>
              <li><strong>Haz clic en "Nuevo Flujo"</strong> en la parte superior derecha.</li>
              <li><strong>Completa los campos:</strong>
                <ul>
                  <li><strong>Nombre del Flujo:</strong> Un nombre descriptivo (ej: "Bienvenida", "Soporte Técnico")</li>
                  <li><strong>Descripción:</strong> Explica qué hace este flujo (opcional pero recomendado)</li>
                  <li><strong>Palabra Clave de Activación:</strong> La palabra que activará el flujo cuando un usuario la envíe (ej: "hola", "ayuda", "info")</li>
                </ul>
              </li>
              <li><strong>Define el Flujo (JSON):</strong> Este es el corazón del chatbot. Debes crear un objeto JSON con la estructura de pasos.</li>
            </ol>
          </div>

          <div className="instruction-section">
            <h4>3. Estructura del Flujo (JSON)</h4>
            <p>El campo "Datos del Flujo" debe contener un objeto JSON válido con la siguiente estructura:</p>
            <pre className="code-example">{`{
  "steps": [
    {
      "id": 1,
      "type": "message",
      "content": "¡Hola! Bienvenido a nuestro servicio. ¿En qué puedo ayudarte?",
      "delay": 0
    },
    {
      "id": 2,
      "type": "options",
      "content": "Por favor selecciona una opción:",
      "options": [
        { "text": "1. Información", "value": "info" },
        { "text": "2. Soporte", "value": "support" },
        { "text": "3. Contacto", "value": "contact" }
      ],
      "delay": 1000
    },
    {
      "id": 3,
      "type": "conditional",
      "condition": "{{response}} === 'info'",
      "true_step": {
        "type": "message",
        "content": "Aquí está la información que necesitas..."
      },
      "false_step": {
        "type": "message",
        "content": "Gracias por contactarnos. Te responderemos pronto."
      }
    }
  ]
}`}</pre>
          </div>

          <div className="instruction-section">
            <h4>4. Tipos de Pasos Disponibles</h4>
            <ul>
              <li><strong>message:</strong> Envía un mensaje de texto simple
                <pre className="code-example">{`{
  "type": "message",
  "content": "Tu mensaje aquí",
  "delay": 1000
}`}</pre>
              </li>
              <li><strong>options:</strong> Muestra opciones para que el usuario seleccione
                <pre className="code-example">{`{
  "type": "options",
  "content": "Selecciona una opción:",
  "options": [
    { "text": "Opción 1", "value": "opt1" },
    { "text": "Opción 2", "value": "opt2" }
  ],
  "delay": 1000
}`}</pre>
              </li>
              <li><strong>conditional:</strong> Ejecuta diferentes pasos según una condición
                <pre className="code-example">{`{
  "type": "conditional",
  "condition": "{{variable}} === 'valor'",
  "true_step": { "type": "message", "content": "Si es verdadero" },
  "false_step": { "type": "message", "content": "Si es falso" }
}`}</pre>
              </li>
              <li><strong>input:</strong> Solicita información al usuario
                <pre className="code-example">{`{
  "type": "input",
  "prompt": "Por favor ingresa tu nombre:",
  "variable": "user_name",
  "delay": 1000
}`}</pre>
              </li>
            </ul>
          </div>

          <div className="instruction-section">
            <h4>5. Editar un Flujo Existente</h4>
            <ol>
              <li>Encuentra el flujo que deseas editar en la lista.</li>
              <li>Haz clic en el botón <strong>"Editar"</strong> en la tarjeta del flujo.</li>
              <li>Modifica los campos que necesites cambiar.</li>
              <li>Haz clic en <strong>"Guardar"</strong> para aplicar los cambios.</li>
            </ol>
          </div>

          <div className="instruction-section">
            <h4>6. Ejemplo Completo: Flujo de Bienvenida</h4>
            <pre className="code-example">{`{
  "name": "Bienvenida",
  "description": "Flujo de bienvenida para nuevos usuarios",
  "trigger_keyword": "hola",
  "is_active": true,
  "flow_data": {
    "steps": [
      {
        "id": 1,
        "type": "message",
        "content": "¡Hola! 👋 Bienvenido a nuestro servicio. Estoy aquí para ayudarte.",
        "delay": 0
      },
      {
        "id": 2,
        "type": "message",
        "content": "¿En qué puedo asistirte hoy?",
        "delay": 1500
      },
      {
        "id": 3,
        "type": "options",
        "content": "Selecciona una opción:",
        "options": [
          { "text": "1️⃣ Información general", "value": "info" },
          { "text": "2️⃣ Soporte técnico", "value": "support" },
          { "text": "3️⃣ Contactar con un agente", "value": "agent" }
        ],
        "delay": 1000
      }
    ]
  }
}`}</pre>
          </div>

          <div className="instruction-section">
            <h4>7. Consejos y Buenas Prácticas</h4>
            <ul>
              <li>✅ <strong>Usa palabras clave simples:</strong> Evita caracteres especiales y espacios.</li>
              <li>✅ <strong>Valida tu JSON:</strong> Asegúrate de que el JSON sea válido antes de guardar. Puedes usar herramientas online como jsonlint.com.</li>
              <li>✅ <strong>Prueba el flujo:</strong> Después de crear un flujo, envíale un mensaje con la palabra clave para verificar que funcione correctamente.</li>
              <li>✅ <strong>Usa delays apropiados:</strong> Los delays (en milisegundos) hacen que el chatbot parezca más natural.</li>
              <li>✅ <strong>Mantén los mensajes cortos:</strong> Los mensajes largos pueden ser difíciles de leer en WhatsApp.</li>
              <li>✅ <strong>Activa/Desactiva flujos:</strong> Usa el checkbox "Activo" para habilitar o deshabilitar flujos sin eliminarlos.</li>
            </ul>
          </div>

          <div className="instruction-section">
            <h4>8. Solución de Problemas</h4>
            <ul>
              <li><strong>El flujo no se activa:</strong> Verifica que la palabra clave coincida exactamente (sin mayúsculas/minúsculas).</li>
              <li><strong>Error de JSON:</strong> Asegúrate de que todas las comillas estén cerradas y que la sintaxis sea correcta.</li>
              <li><strong>El flujo está inactivo:</strong> Verifica que el checkbox "Activo" esté marcado.</li>
              <li><strong>Los mensajes no se envían:</strong> Verifica que Evolution API esté configurado y conectado correctamente.</li>
            </ul>
          </div>

          <div className="instruction-section">
            <h4>9. Variables Disponibles</h4>
            <p>Puedes usar variables en tus mensajes para personalizarlos:</p>
            <ul>
              <li><code>{'{{user_name}}'}</code> - Nombre del usuario</li>
              <li><code>{'{{user_phone}}'}</code> - Teléfono del usuario</li>
              <li><code>{'{{response}}'}</code> - Respuesta del usuario en pasos anteriores</li>
              <li><code>{'{{date}}'}</code> - Fecha actual</li>
              <li><code>{'{{time}}'}</code> - Hora actual</li>
            </ul>
          </div>
        </div>
      )}

      {error && <div className="error-message">{error}</div>}

      {showForm && (
        <div className="form-modal">
          <div className="form-content">
            <h3>{editingFlow ? 'Editar Flujo' : 'Nuevo Flujo'}</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Nombre del Flujo</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Descripción</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                />
              </div>
              <div className="form-group">
                <label>Palabra Clave de Activación</label>
                <input
                  type="text"
                  value={formData.trigger_keyword}
                  onChange={(e) => setFormData({ ...formData, trigger_keyword: e.target.value })}
                  placeholder="Ej: ayuda, soporte, info"
                />
              </div>
              <div className="form-group">
                <label>Datos del Flujo (JSON)</label>
                <textarea
                  value={JSON.stringify(formData.flow_data, null, 2)}
                  onChange={(e) => {
                    try {
                      setFormData({ ...formData, flow_data: JSON.parse(e.target.value) });
                    } catch (err) {
                      // Invalid JSON, ignore
                    }
                  }}
                  rows={10}
                  style={{ fontFamily: 'monospace' }}
                />
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                  {' '}Activo
                </label>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn-primary">Guardar</button>
                <button type="button" onClick={() => { setShowForm(false); setEditingFlow(null); }} className="btn-secondary">Cancelar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {loading ? (
        <div>Cargando...</div>
      ) : (
        <div className="flows-grid">
          {flows.map((flow) => (
            <div key={flow.id} className="flow-card">
              <div className="flow-header">
                <h3>{flow.name}</h3>
                <span className={`status-badge ${flow.is_active ? 'active' : 'inactive'}`}>
                  {flow.is_active ? 'Activo' : 'Inactivo'}
                </span>
              </div>
              {flow.description && <p className="flow-description">{flow.description}</p>}
              {flow.trigger_keyword && (
                <div className="flow-trigger">
                  <strong>Palabra clave:</strong> {flow.trigger_keyword}
                </div>
              )}
              <div className="flow-actions">
                <button onClick={() => handleEdit(flow)} className="btn-edit">Editar</button>
                <button onClick={() => handleDelete(flow.id!)} className="btn-delete">Eliminar</button>
              </div>
            </div>
          ))}
          {flows.length === 0 && (
            <div className="empty-state">No hay flujos configurados. Crea uno nuevo para comenzar.</div>
          )}
        </div>
      )}
    </div>
  );
};

export default ChatbotFlows;

